<template>
  <DialogHeader>
    <template #right>
      <a
        href="https://dacr.zendesk.com/hc/en-us"
        target="_blank"
        class="font-medium text-green-900 cursor-pointer"
      >
        Help
      </a>
    </template>
    Upload details
  </DialogHeader>
  <DialogContent
    class="overflow-y-auto mx-[-22px] tablet-h:mx-[-30px] h-[300px] max-h-[300px]"
  >
    <div
      v-for="upload in props.uploads"
      :key="upload.id"
      class="w-full min-h-[50px] first:border-t border-b border-gray-800/8 px-[22px] tablet-h:px-[30px] py-2.5"
    >
      <div class="w-full flex items-center justify-between">
        <div class="flex items-center grow gap-x-3">
          <Icon
            v-if="hasUploadSucceeded(upload)"
            :size="30"
            name="FilledCircleCheck"
          />
          <div
            v-else-if="hasUploadFailed(upload)"
            class="min-w-[30px] max-w-[30px] h-[30px] flex justify-center items-center bg-red-200/50 rounded-full"
          >
            <Icon :size="16" name="Alert" class="stroke-red-700" />
          </div>
          <CircularProgressIndicator
            v-else
            :percentage="upload.percentage"
            strokeColor="#34B1C7"
            class="border-gray-200/30!"
          />
          <div
            v-truncate
            class="w-full max-w-[350px] font-secondary text-[14px]"
          >
            <div data-truncate-target>
              {{ upload.fileName }}
            </div>
          </div>
        </div>
        <div class="font-secondary">
          <div v-if="upload.error" class="text-red-600 leading-[20px]">
            {{ upload.canceled ? 'Upload cancelled' : 'Upload failed' }}
          </div>
          <div v-else class="flex items-center">
            <div
              v-if="upload.timeLeft"
              class="flex items-center font-secondary text-[14px] opacity-50"
            >
              <div>{{ formatTimeLeft(upload.timeLeft) }}</div>
            </div>
            <Icon
              v-if="!hasUploadSettled(upload)"
              name="Plus"
              :size="18"
              class="ml-2.5 rotate-45 stroke-gray-800 cursor-pointer"
              @click="handleCancelUpload(upload)"
            />
          </div>
        </div>
      </div>
      <div v-if="upload.error && !upload.canceled" class="pl-[42px]">
        <Message class="mt-2.5" type="error" icon="none">
          {{ getError(upload.error) }}
        </Message>
      </div>
    </div>
  </DialogContent>
  <DialogFooter class="mt-auto! pt-6">
    <div class="w-full text-left font-primary text-[14px]">
      Having problems? See our
      <a
        href="https://dacr.zendesk.com/hc/en-us"
        target="_blank"
        class="text-link"
      >
        Help Center
      </a>
    </div>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import CircularProgressIndicator from '@/shared/components/CircularProgressIndicator.vue'
import type { Upload } from '@/features/upload/store'
import { formatTimeLeft } from '@/shared/utils/datetime'
import { Icon, Message } from '@dacr/ui'
import { ErrorCodes } from '@/shared/graphql/legacy/generated'

const props = defineProps<{
  uploads: Upload[]
}>()

const { open } = useDialog()

const hasUploadSucceeded = (upload: Upload) =>
  upload.percentage === 100 && !upload.error

const hasUploadFailed = (upload: Upload) => upload.error

const hasUploadSettled = (upload: Upload) =>
  hasUploadSucceeded(upload) || hasUploadFailed(upload)

const getError = (error: ErrorCodes) => {
  if (
    error === ErrorCodes.PaymentFailed ||
    error === ErrorCodes.PaymentMethodInvalid ||
    error === ErrorCodes.PaymentActionRequired
  ) {
    return 'There is an issue with your payment method. Please fix your payment to continue using Dacr.'
  }

  if (error === ErrorCodes.LimitExceeded) {
    return 'You have exceeded the limit of files you can upload. Please upgrade your plan to continue using Dacr.'
  }

  if (error === ErrorCodes.UploadFileSizeExceededError) {
    return 'File size is too large. The maximum file size that you can add to Dacr is 2GB. Please compress the file and try again.'
  }

  return 'Upload failed due to a technical issue. Please try again or contact us.'
}

const handleCancelUpload = (upload: Upload) => {
  open(Dialogs.CancelUpload, {
    props: { upload },
  })
}
</script>
